import moment from "moment";
import pluralize from "pluralize";

import { ColumnDueDate } from "./compliance-program-progress-constants";

/**
 * A Progress tab column header: the step, and when it fell due.
 *
 * The date sits here rather than on the cells because it belongs to the step, not to the
 * person: on an ordinary review every late cell in the column would otherwise carry the
 * same figure. It stays grey whatever the state of the column - lateness is the timer's
 * job, beside the cell that is late, and a red header repeated the same alarm above it.
 *
 * An ongoing review starts each person on their own hire date, so a step there has no
 * single due date. It states the rule instead - "due 30 days after start date" - which is
 * what the admin configured and what every person's date is counted from. If even the
 * rule differs, the column says nothing rather than something vague; one person's own
 * date is always in the card their cell opens.
 *
 * That rule is longer than a column is wide, so both lines truncate and the date line
 * carries its full text as a title. A header that widened itself would push the status
 * columns out of line with the tables above and below it.
 */
const ProgramProgressColumnHeader = ({
  label,
  due,
}: {
  label: string;
  due?: ColumnDueDate | null;
}) => {
  const text = due?.varies
    ? due.afterStartDays === null
      ? null
      : `due ${pluralize("day", due.afterStartDays, true)} after start date`
    : due
      ? `due ${moment(due.dueDate).format("D MMM")}`
      : null;

  if (!text) {
    return <>{label}</>;
  }
  return (
    <div className="flex flex-col min-w-0">
      <span className="truncate" title={label}>
        {label}
      </span>
      <span
        className="truncate font-normal text-2xs text-gray-400 tabular-nums"
        title={text}
      >
        {text}
      </span>
    </div>
  );
};

export default ProgramProgressColumnHeader;
